import { dayOf, mondayOfWeek, shiftDays } from '@ghp/domain'
import { z } from 'zod'
import type { PlanningWeekRequest } from '@ghp/api-client'

import { urlText } from '../../../lib/search-params'

/**
 * Le modèle de l'écran « Planning » : le schéma des `search params`, les deux vues, et la
 * **couture** vers la requête serveur. Hors de la route pour la même raison qu'ailleurs — la
 * déclaration de route importe ce module, alors que l'écran est chargé à la demande.
 *
 * ⚠️ **L'URL est l'état, et la semaine y entre** — elle vivait en état local tant que l'écran
 * montrait des fixtures. Deux raisons de l'y mettre maintenant : la semaine affichée **est** la
 * clé de cache de la requête, et un planificateur qui envoie « regarde la semaine du 21 » n'a
 * qu'un lien à coller. Chaque champ porte son `.catch` (M21 appliqué à l'URL).
 */

/**
 * Les deux lectures de la même semaine.
 *
 * ⚠️ **`teams` est un ajout au prototype validé**, qui n'a que la grille heures × jours. Elle
 * répond à la question du planificateur — « qui fait quoi cette semaine ? » — sans compter les
 * pastilles. La vue du prototype reste disponible et rien n'y est retiré : c'est un ajout, pas un
 * remplacement. À faire arbitrer en revue.
 */
export const PLANNING_VIEWS = [
  { value: 'teams', label: 'Par équipe' },
  { value: 'slots', label: 'Par créneau' },
] as const

export type PlanningView = (typeof PLANNING_VIEWS)[number]['value']

const PLANNING_VIEW_VALUES = PLANNING_VIEWS.map(({ value }) => value) as [
  PlanningView,
  ...PlanningView[],
]

/**
 * Le jour désigne-t-il une date réelle ?
 *
 * ⚠️ **Les deux gardes comptent, et dans cet ordre.** Zod n'interrompt pas sa chaîne de
 * vérifications : `'lundi'` atteint ce prédicat malgré le `regex` qui le précède, et une exception
 * levée ici **ne retombe pas** sur le `.catch` du schéma — d'où le test `Number.isNaN` d'abord.
 *
 * ⚠️ Ensuite, la validité se vérifie **par aller-retour** : `new Date('2026-02-31T12:00:00Z')` ne
 * rend pas `Invalid Date`, il **décale** au 3 mars. Un jour impossible ouvrirait donc une semaine
 * parfaitement valide, mais pas celle demandée — le défaut muet que `parisInstant` écarte déjà
 * côté domaine, ici sous une autre forme.
 */
function isRealDay(day: string): boolean {
  const at = new Date(`${day}T12:00:00.000Z`)

  return !Number.isNaN(at.getTime()) && at.toISOString().startsWith(day)
}

/**
 * Le lundi de la semaine affichée, en jour ISO.
 *
 * ⚠️ **Toute valeur est ramenée à un lundi**, y compris celle qu'un humain tape dans l'URL : la
 * grille part du lundi, et une origine au mercredi décalerait ses sept colonnes sans que rien ne
 * le signale. Un jour illisible retombe sur la semaine courante — M21, on n'affiche pas un écran
 * vide pour un paramètre que personne n'a choisi.
 */
const weekSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u)
  .refine(isRealDay)
  .transform(mondayOfWeek)
  .optional()
  .catch(undefined)

export const planningSearchSchema = z.object({
  week: weekSchema,
  view: z.enum(PLANNING_VIEW_VALUES).default('teams').catch('teams'),
  /** `all`, ou l'identifiant d'une équipe — jamais son nom, qu'un renommage change (M22). */
  teamId: z.string().default('all').catch('all'),
  /** Adresse, commune ou référence — la grille **et** le rail « À planifier » (#357). */
  q: urlText(''),
})

export type PlanningSearch = z.infer<typeof planningSearchSchema>

export const PLANNING_DEFAULTS: PlanningSearch = planningSearchSchema.parse({})

/** Combien de jours la grille montre — du lundi au dimanche, comme le prototype validé. */
export const PLANNING_WEEK_LENGTH = 7

/**
 * Le lundi de la semaine affichée : celui de l'URL, ou celui d'aujourd'hui.
 *
 * ⚠️ **`dayOf` et non `toLocaleDateString`** : le jour est lu au fuseau de Paris, pas à celui du
 * poste. Un navigateur réglé à Tokyo ouvrirait sinon la semaine suivante un dimanche soir — et le
 * serveur, lui, répondrait sur celle de Paris.
 *
 * ⚠️ L'instant est un **paramètre**, jamais un `new Date()` interne : un test qui ne peut pas
 * figer l'horloge d'une fonction ne peut pas l'exercer (#107, #165).
 */
export function weekStartOf(search: PlanningSearch, now: Date): string {
  return search.week ?? mondayOfWeek(dayOf(now))
}

/** Les sept jours de la grille, du lundi au dimanche — les colonnes, dans l'ordre. */
export function weekDaysFrom(monday: string): string[] {
  return Array.from({ length: PLANNING_WEEK_LENGTH }, (_, index) => shiftDays(monday, index))
}

/**
 * **La couture** : l'état d'URL vers ce que l'API accepte (D21).
 *
 * ⚠️ **`to` est le lundi suivant, et non le dimanche** : la fenêtre du serveur est semi-ouverte —
 * une borne au dimanche perdrait toute la dernière journée de la grille.
 */
export function toPlanningWeekQuery(search: PlanningSearch, now: Date): PlanningWeekRequest {
  const from = weekStartOf(search, now)

  return {
    from,
    to: shiftDays(from, PLANNING_WEEK_LENGTH),
    teamId: search.teamId,
    search: search.q.trim(),
  }
}

/**
 * L'équipe que la boîte de dépôt préremplit : celle de la ligne visée, sinon la première que le
 * filtre d'équipe garde. Sans filtre (`filtered` vide), seule la ligne visée en nomme une.
 *
 * ⚠️ Un défaut, pas une affectation : la boîte laisse la changer avant de poser. Un planning filtré
 * sur une équipe qui laisserait le rendez-vous sans elle changerait l'affichage sans le geste.
 */
export function droppedTeam<T>(target: T | null, filtered: readonly T[]): T | null {
  return target ?? filtered[0] ?? null
}
